import Button from "@components/Button";
import CustomInput, {
  CustomSelectInput,
} from "@components/CustomInput/CustomInput";
import TableComponent from "@components/TableComponent/TableComponent";
import { Home } from "@mui/icons-material";
import styles from "./ManagePatientPage.module.css";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import usePatient from "@hooks/usePatient";
import { useSelector } from "react-redux";

const ManagePatientPage = () => {
  const navigate = useNavigate();
  const { handleGetPatient } = usePatient();
  const { patientData } = useSelector((state) => state.patient);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const [searchFilters, setSearchFilters] = useState({
    referenceNumber: "",
    firstName: "",
    contact: "",
    email: "",
    pincode: "",
    order: "",
    subscriptionStartDate: "",
  });

  const handleNavigatePatientDetail = (email) => {
    navigate(`/patient/${email}`);
  };

  const handleSearchInput = ({ target }) => {
    const { name, value } = target;

    setSearchFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const tableHeads = [
    {
      component: (
        <CustomInput
          align={"left"}
          className={styles.Input}
          showSearchIcon
          name="firstName"
          value={searchFilters.firstName}
          onChange={handleSearchInput}
        />
      ),
    },
    {
      component: (
        <CustomInput
          align={"left"}
          className={styles.Input}
          showSearchIcon
          name="contact"
          value={searchFilters.contact}
          onChange={handleSearchInput}
        />
      ),
    },

    {
      component: (
        <CustomInput
          align={"left"}
          className={styles.Input}
          showSearchIcon
          name="email"
          value={searchFilters.email}
          onChange={handleSearchInput}
        />
      ),
    },
    {
      component: (
        <CustomInput
          align={"left"}
          className={styles.Input}
          showSearchIcon
          name="subscriptionStartDate"
          value={searchFilters.subscriptionStartDate}
          onChange={handleSearchInput}
        />
      ),
    },
    {
      component: (
        <CustomInput
          align={"left"}
          className={styles.Input}
          showSearchIcon
          name="subscriptionDays"
          value={searchFilters?.subscriptionDays}
          onChange={handleSearchInput}
        />
      ),
    },
    {
      component: (
        <CustomInput
          align={"left"}
          className={styles.Input}
          showSearchIcon
          name="referenceNumber"
          value={searchFilters.referenceNumber}
          onChange={handleSearchInput}
        />
      ),
    },
    { component: <div></div> },
  ];

  const columns = [
    { id: "firstName", label: "First Name", minWidth: 170 },
    {
      id: "contact",
      label: "Contact",
      minWidth: 170,
      align: "left",
      format: (value) => value.toLocaleString("en-US"),
    },
    {
      id: "email",
      label: "Email",
      minWidth: 170,
      align: "left",
      format: (value) => value.toLocaleString("en-US"),
    },
    {
      id: "subscriptionStartDate",
      label: "Start",
      minWidth: 170,
      align: "left",
      format: (value) => value.toFixed(2),
    },
    {
      id: "subscriptionDays",
      label: "Days",
      minWidth: 170,
      align: "left",
      format: (value) => value.toFixed(2),
    },
    {
      id: "refNo",
      label: "Reference Number",
      minWidth: 170,
      align: "left",
      format: (value) => value.toFixed(2),
    },
    {
      id: "activePlan",
      label: "Active",
      minWidth: 170,
      align: "left",
      format: (value) => value.toFixed(2),
    },

    {
      id: "view",
      label: "View",
      minWidth: 170,
      align: "left",
      format: (value) => value.toFixed(2),
    },
  ];

  const getActivePlan = (subscriptionStartDate, subscriptionDays) => {
    const currentDate = new Date();
    const subscriptionEndDate = new Date(subscriptionStartDate);
    subscriptionEndDate.setDate(
      subscriptionEndDate.getDate() + subscriptionDays
    );

    if (currentDate > subscriptionEndDate) {
      return "❌";
    } else return "✅";
  };

  // name,contact,email,subscriptionStartDate,subscriptionDays,referenceNo,view
  function createData(
    firstName,
    contact,
    email,
    subscriptionStartDate,
    subscriptionDays,
    refNo
  ) {
    return {
      firstName,
      contact,
      email,
      subscriptionStartDate,
      subscriptionDays,
      refNo,
      activePlan: getActivePlan(subscriptionStartDate, subscriptionDays),
      view: (
        <Button
          text={"view"}
          onClick={() => handleNavigatePatientDetail(email)}
        />
      ),
    };
  }

  const rows = patientData?.data?.map((patient) => {
    return createData(
      patient.local.firstName,
      patient.local.contact,
      patient.local.email,
      patient.subscriptionStartDate,
      patient.subscriptionDays,
      patient.referenceNumber
    );
  });

  const sortByOptions = [
    { option: "First Name", value: "local.firstName" },
    { option: "Email", value: "local.email" },
    { option: "Created At", value: "created_at" },
    { option: "Updated At", value: "updated_at" },
  ];
  const orderByOptions = [
    { option: "Ascending", value: 1 },
    { option: "Descending", value: -1 },
  ];

  const handleNavigateDashboard = () => {
    navigate("/");
  };

  const handleNavigateDoctorPanel = () => {
    navigate("/managedoctor");
  };

  const searchFunction = () => {
    handleGetPatient({ ...searchFilters, page: page + 1, limit: rowsPerPage });
  };

  useEffect(() => {
    handleGetPatient({ ...searchFilters, page: page + 1, limit: rowsPerPage });
  }, [page, rowsPerPage, searchFilters.order, searchFilters?.sortBy]);

  return (
    <div>
      <div className={styles.MenuWrapper}>
        <div className={styles.NavigationWrapper}>
          <Home className={styles.HomeIcon} onClick={handleNavigateDashboard} />{" "}
          <div>
            <span
              className={styles.DashboardText}
              onClick={handleNavigateDashboard}
            >
              Dashboard
            </span>
            <span className={styles.RouteText}>/Patient Panel</span>
          </div>
        </div>
        <div className={styles.ActionItemsWrapper}>
          <Button
            className={styles.BtnClass}
            text={"Manage Doctor"}
            btnType={"secondary"}
            onClick={handleNavigateDoctorPanel}
          />
          <CustomSelectInput
            className={styles.CustomSelect}
            selectOptions={sortByOptions}
            defaultOption={"Sort By"}
            value={searchFilters.sortBy}
            onChange={handleSearchInput}
            name={"sortBy"}
          />

          <CustomSelectInput
            selectOptions={orderByOptions}
            className={styles.CustomSelect}
            defaultOption={"Order By"}
            name={"order"}
            value={searchFilters.order}
            onChange={handleSearchInput}
          />
        </div>
      </div>
      <TableComponent
        tableHeads={tableHeads}
        columns={columns}
        rows={rows}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        page={page}
        setPage={setPage}
        totalRecords={patientData?.total_record ?? 0}
        searchFunction={searchFunction}
      />
    </div>
  );
};
export default ManagePatientPage;
